import React from "react";
import styled from "styled-components";
import { colors, font, fontSizing, media, spacing } from "../theme";
import Headline, { HeadingLevel } from "../common/Headline";
import Card from "../common/Card";
import Label from "../common/Label";
import Button from "../common/Button";
import Grid from "../common/Grid";
import { RouteName, RouteParams } from "../../lib/routes";
import Link from "../utils/Link";

const StyledCard = styled(Card)<{ external?: boolean }>`
  box-shadow: 0 4px 4px 0 #dee5e9;
  background: ${p => (p.external ? colors.lightGrey : colors.paleGreyTwo)};
`;

const Abstract = styled(Label)`
  color: ${colors.blue};
  ${spacing("margin-bottom", 16)}
`;

const KeyFactList = styled.ul`
  list-style: none;
  margin-top: 0;
  padding-left: 0;
`;

const KeyFact = styled.li`
  display: flex;
  color: ${colors.blue};
  font-weight: ${font.weightRegular};
  ${fontSizing("label", 20)}
  ${spacing("margin-bottom", 16)}
    &::before {
    content: "\\2022";
    color: ${colors.pigeonLight};
    font-weight: bold;
    display: inline-block;
    min-width: 1em;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: calc(4px * 10) calc(4px * 12);
  height: 100%;
  max-width: 100%;
  > * {
    width: 100%;
  }

  @media screen and ${media.superTinyZoom125} {
    padding: calc(4px * 10 / 2) calc(4px * 12 / 2);
  }
`;
const StyledButton = styled(Button)`
  width: auto;
`;
const MarginTopButton = styled(StyledButton)`
  margin-top: auto;
`;
const StyledLabel = styled(Label)`
  margin-top: auto;
`;

type ProgramTile = {
  label: string;
  headline: string;
  abstract: string;
  keyFacts: string[];
  ctaLabel: string;
  headlineLevel?: HeadingLevel;
} & (
  | { ctaHref: string; external: true }
  | { route: RouteName; params?: RouteParams; external: false });

export const ProgramTile = (p: ProgramTile) => {
  const BTN = p.external ? StyledButton : MarginTopButton;
  const linkAttrs = {
    href: "ctaHref" in p ? p.ctaHref : undefined,
    route: "route" in p ? p.route : undefined,
    params: "params" in p ? p.params : undefined
  };
  return (
    <StyledCard external={p.external}>
      <Content>
        <Label small>{p.label}</Label>
        <Headline size={4} bold level={p.headlineLevel || 3}>
          <Link {...linkAttrs}>
            <a tabIndex={-1}>{p.headline}</a>
          </Link>
        </Headline>
        <Abstract tiny>{p.abstract}</Abstract>
        <KeyFactList>
          {p.keyFacts.map((k, idx) => (
            <KeyFact key={idx}>{k}</KeyFact>
          ))}
        </KeyFactList>
        {/*p.external && (
          <StyledLabel tiny>
            Über den folgenden Button verlassen Sie den Internetauftritt der IFB
            Hamburg:
          </StyledLabel>
        )*/}
        <BTN {...linkAttrs} small aria-label={p.headline}>
          {p.ctaLabel}
        </BTN>
      </Content>
    </StyledCard>
  );
};

interface Props {
  programs: ProgramTile[];
  headlineLevel?: HeadingLevel;
}
const ProgramGrid = (p: Props) => {
  const { programs } = p;
  if (!programs) return null;
  return (
    <Grid columns={3} tabletColumns={2} mobileColumns={1}>
      {programs.map((tile, idx) => (
        <ProgramTile key={idx} {...tile} headlineLevel={p.headlineLevel} />
      ))}
    </Grid>
  );
};

export default ProgramGrid;
